// Copyright 2017-2025 @polkadot/apps-config authors & contributors
// SPDX-License-Identifier: Apache-2.0

import SQUID_CONFIG from '../../../../config/squid.json' with { type: 'json' };
import DUNITER_CONFIG from '../../../../config/duniter.json' with { type: 'json' };

interface NetworkConfig {
  genesisHash: `0x${string}`;
  providers: Record<string, `wss://${string}`>;
  squid: string[];
}

const configCache: Map<string, Promise<NetworkConfig>> = new Map();

async function fetchAndCache(networkName: string): Promise<NetworkConfig> {
  const url = `https://corsproxy.io/?https://git.duniter.org/nodes/networks/-/raw/master/${networkName}.json`;
  
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch config from ${url}`);
    }
    const data = await response.json();
    console.log(`Successfully fetched remote config for ${networkName}`);
    // Assuming the remote structure has a 'squid' and 'rpc' array, and 'genesis_hash'.
    const config = {
      genesisHash: data.genesis_hash,
      providers: data.rpc.reduce((acc: Record<string, any>, url: string) => {
        // We need provider names, remote file doesn't have them.
        // We'll generate them for now.
        const name = new URL(url).hostname;
        acc[name] = url;
        return acc;
      }, {}),
      squid: data.squid
    };
    localStorage.setItem(`networkConfig-${networkName}`, JSON.stringify(config));
    return config;
  } catch (error) {
    console.warn(`Failed to fetch remote config for ${networkName}, using local fallback. Error: ${error}`);
    const duniterConf = (DUNITER_CONFIG as Record<string, any>)[networkName];
    const squidConf = (SQUID_CONFIG as Record<string, any>)[networkName];

    if (!duniterConf || !squidConf) {
      throw new Error(`No local fallback configuration found for network: ${networkName}`);
    }

    return {
      genesisHash: duniterConf.genesisHash,
      providers: duniterConf.providers,
      squid: squidConf.fallbackEndpoints
    };
  }
}

export function getNetworkConfig(networkName: string): NetworkConfig | null {
  const configStr = localStorage.getItem(`networkConfig-${networkName}`);
  if (configStr) {
    return JSON.parse(configStr) as NetworkConfig;
  }
  return null;
}

export function loadNetworkConfig(networkName: string): Promise<NetworkConfig> {
  if (!configCache.has(networkName)) {
    configCache.set(networkName, fetchAndCache(networkName));
  }
  return configCache.get(networkName)!;
} 